/* eslint-disable no-restricted-imports */
import { nanoid } from 'nanoid';
import { v4 as uuidv4 } from 'uuid';
import { isMobileOnly } from 'react-device-detect';
import { cloneDeep } from 'lodash-es';

import logger from '@shared/utils/error/logger';
import Tourist from '@shared/utils/tourist';
import { LOGGER_SPACIAL_SYMBOL } from '@shared/utils/error/config';
import { A1_DATA_WAREHOUSE } from '@shared/env';
import { localAESStorage, sessionAESStorage } from '@shared/utils/storage';
import { post } from '@shared/utils/http';
import { LSK, SSK } from '@shared/utils/storage/config';
import {
	detectEnvironmentType,
	isA1App,
	isGoogleBot,
} from '@shared/utils/platform/platformDetection';
import { isOpenDebugMode } from '@shared/env';
import { isBuildPage } from '@shared/utils/url/match';
import { useUserCreate } from '@shared/stores/User';
import { trackRecorder } from '@shared/utils/event/base/recorder';
import { HttpDependency } from '@shared/utils/http/dependency';
import { loadOneTimeData } from '@shared/utils/storage/onetime';
import { getDevice, getUTC8TimeString } from '@shared/utils/tracker/baidu';
import { ParamsEncryption } from '@shared/utils/encrypt';

import { actionToTheme, LoggingAction, Theme, type TrackObjectType } from './types';
import { FeatureAtomType } from '@/mobile/views/Studio/config';
import { tryParseJson } from '@shared/utils/json';
import { userBehavior } from '@shared/stores/UserBehavior';
import { UserChannelVersionMap, useUserTypeStore } from '@/mobile/store/UserType';
import { getUserVipStatus } from '@shared/stores/UserCredit/tools';
import { getRecommendPageInfo, getRecommendSearchParams } from '@/handler/cache/recommend';
import { checkIsRecommendAppByData } from '@/mobile/views/AppFeed/hooks/useInsertTopApp';
import { getAeoGroupingId } from '@shared/utils/aeo';
import { businessType, LowercaseProduct } from '@shared/matrix/const';
import { isLongVideoFilter, isVideoFilterType } from '@shared/utils/app/transform';
import { AppCategoryType } from '@shared/typings/models/app';

export { LoggingAction };

export namespace eventTrackingApi {
	export interface Option {
		business_type: string;
		theme: string | null;
		action: string;
		distinct_id: string;
		time: number | string;
		user_id: string | number | undefined;
		hrefUrl: string;
		uniqueId: string | null;
		sessionId?: string;
		count?: number;
		properties: {
			user_version: string;
			[key: string]: any; // 其他个性化字段
		};
	}
}

export const getCurrentTab = (): string => {
	const isFromPlay = Boolean(localStorage.getItem(LSK.IS_FROM_INDEX_PLAY));
	if (isFromPlay) {
		return 'play';
	}
	const currentTab = sessionAESStorage.getItem(SSK.CURRENT_TAB) || 'share';
	return currentTab;
};

const mainUrlList = ['app', 'image', 'collection', '/'];

export const setCurrentTab = (pathname: string): void => {
	const defaultTab = isMobileOnly ? 'app' : 'image';
	if (mainUrlList.some((item) => pathname.endsWith(item))) {
		// 将pathname切割，取最后一个元素，这样拿到的tabName就不受多语言影响
		const currentTabName = pathname.split('/').pop();
		sessionAESStorage.setItem(SSK.CURRENT_TAB, currentTabName || defaultTab);
	}
};

const ROUTE_MAPPINGS = [
	{ match: 'exact', path: '/post', tab: 'discover' },
	{ match: 'exact', path: '/', tab: 'creator' },
	{ match: 'exact', path: '/tag', tab: 'topic' },
	{ match: 'include', path: '/user', tab: 'profile' },
	{ match: 'include', path: '/search', tab: 'search' },
];

export const setCurrentTabByPath = (currentPath: string) => {
	const defaultTab = 'home';
	const matchedRule = ROUTE_MAPPINGS.find((rule) => {
		return rule.match === 'exact' ? currentPath === rule.path : currentPath.includes(rule.path);
	});

	sessionAESStorage.setItem(SSK.CURRENT_TAB, matchedRule?.tab || defaultTab);
};
export const getClickObject = (): string => {
	const previousPathname = sessionAESStorage.getItem(SSK.PREVIOUS_PATHNAME) || '';
	return mainUrlList.some((item) => previousPathname?.endsWith(item)) ? 'main' : 'recommend';
};

export const getLabelData = () => {
	if (isMobileOnly) {
		return trackRecorder.source;
	}
	const labelName = sessionStorage.getItem(SSK.LABEL_NAME) || 'none';
	const labelId = sessionStorage.getItem(SSK.LABEL_ID) || 'none';
	return { labelName, labelId };
};

export const getObjectType = ({
	isVideo = false,
	appCategory = '0',
}: {
	isVideo?: boolean;
	appCategory?: AppCategoryType;
}): TrackObjectType => {
	if (appCategory === '4') return 'Digital human';
	if (isLongVideoFilter(appCategory)) return 'short film';
	if (isVideo || isVideoFilterType(appCategory)) return 'video';
	return 'image';
};

const SINGLE_SHOT_TYPE: LoggingAction[] = [
	LoggingAction.REGISTER,
	LoggingAction.USERPATH,
	LoggingAction.Add_To_Desktop_Load_success,
];

//登陆后才能进行的埋点操作
enum LOGIN_ACTION {
	OPTIMIZE_SUCCESS = 'optimize_success',
	OPTIMIZE = 'optimize',
	SAVE_APP = 'save_app',
	FORK_APP = 'fork_app',
	IMAGE_PUBLISH = 'image_publish',
}

const getDefaultEventQueue = () => {
	return tryParseJson(localStorage.getItem(LSK.EVENT_DATA_QUEUE) ?? '[]') || [];
};

class EventTracker {
	private SEND_AS_ARRAY: boolean;
	private eventQueue: eventTrackingApi.Option[] = getDefaultEventQueue(); // 存储埋点数据的数组
	private lastSendTime: number; // 上次发送时间
	private SEND_INTERVAL: number;
	private SEND_COUNT: number;
	private CHECK_INTERVAL: number;
	private isSending: boolean;
	private appVersionCache = '';
	private googleAdIdCache = '';
	public dataWareHouse = {
		location: 'none',
		origin: localAESStorage.getItem(LSK.ORIGIN) || 'none',
		rankLocation: localAESStorage.getItem(LSK.RANK_LOCATION) || 'none',
		userLocation: localAESStorage.getItem(LSK.USER_LOCATION) || 'other',
		subscribeLocation: localAESStorage.getItem(LSK.SUBSCRIBE_LOCATION) || 'none',
		originImageId: 'none',
		traceInfoList: new Map(),
	};

	constructor() {
		this.lastSendTime = Date.now();
		this.isSending = false;
		this.SEND_AS_ARRAY = true; //是否发送埋点数组
		this.SEND_INTERVAL = 5000; //发送间隔单位为毫秒
		this.SEND_COUNT = 5;
		this.CHECK_INTERVAL = 2000; //检查是否满足发送条件，单位为毫秒
		// 定时发送
		setInterval(() => {
			this.checkAndSend();
		}, this.CHECK_INTERVAL);
	}

	private getAppVersion() {
		if (this.appVersionCache) return this.appVersionCache;
		this.appVersionCache = localStorage.getItem(LSK.APP_VERSION) || '';
		return this.appVersionCache;
	}

	private getGoogleAdId() {
		if (this.googleAdIdCache) return this.googleAdIdCache;
		this.googleAdIdCache = localStorage.getItem(LSK.GOOGLE_AD_ID) || '';
		return this.googleAdIdCache;
	}

	public addEventToQueue(event: eventTrackingApi.Option) {
		//将跳过埋点校验，直接硬性加入埋点数组，非特殊情况不建议使用
		this.eventQueue.push(event);
		this.writeEventLog(event);
		this.saveEventQueue();
	}

	public clearEventQueue() {
		this.eventQueue.length = 0;
		this.saveEventQueue();
	}

	public rollbackQueue(events: eventTrackingApi.Option[]) {
		this.eventQueue.unshift(...events);
		this.saveEventQueue();
	}

	public saveEventQueue() {
		localStorage.setItem(LSK.EVENT_DATA_QUEUE, JSON.stringify(this.eventQueue));
	}

	public getEventQueue(): eventTrackingApi.Option[] {
		return this.eventQueue;
	}

	public async loggingEvent(
		action: LoggingAction,
		data: any = {},
		options = { onlyGetData: false },
	) {
		let defaultData = false; // 是否传访问源默认参数
		let paramsData = {}; // 埋点新增属性
		let loggingData = {}; // properties新增属性
		const { labelId, labelName } = getLabelData();
		const product = detectEnvironmentType();
		const onlyGetData = options.onlyGetData ?? false;

		switch (action) {
			case LoggingAction.REGISTRATION_POP_UP: {
				paramsData = { user_id: data.user_id };
				loggingData = {
					isregister: data.isregister,
					location: this.dataWareHouse.location,
					tag_name: labelName || 'none',
					tag_id: labelId || 'none',
				};
				defaultData = true;
				break;
			}
			case LoggingAction.GENERATE_BUTTON_CLICK:
				{
					const ability_name = sessionAESStorage.getItem(SSK.WEB_GENERATE_ABILITY_NAME) || 'a1';
					const newId = uuidv4();
					this.updateLogging({ generateid: newId });
					defaultData = true;
					const isBuildRoute = window.location.pathname.includes('/build') ? 'build' : '';
					const origin =
						isBuildRoute ||
						loadOneTimeData(LSK.QUICK_GENERATE)?.origin ||
						this.dataWareHouse.origin;

					const isSearch = origin === 'search_result' || data.is_search;

					const otherParams = getRecommendSearchParams(isSearch);
					if (origin === 'appgallery') {
						(otherParams as any).gallery_id = data.gallery_id || '';
					}

					loggingData = {
						generateid: newId,
						origin,
						type: data.type,
						ability_name,
						current_tab: loadOneTimeData(data.LSKType)?.currentTab || getCurrentTab(),
						app_id: data.appId || '',
						image_id: this.dataWareHouse.originImageId || '',
						tag_name: labelName,
						tag_id: labelId,
						type_2: loadOneTimeData(LSK.GENERATE_BUTTON_TYPE)?.type_2 || 'none', //是否是vip app
						user_scene: data.user_scene || '',
						trace_info: data.trace_info || this.getTraceInfo(data.appId as string),
						...otherParams,
					};
				}
				break;
			case LoggingAction.OPTIMIZE_SUCCESS:
				loggingData = { pictureid: data.imgId, image_id: data.imgId, sourceId: data.sourceId };
				break;
			case LoggingAction.OPTIMIZE:
				defaultData = true;
				loggingData = {
					image_id: data.imgId,
					pictureid: data.imgId,
					...(data.origin && { origin: data.origin }),
					...(data.app_id && { app_id: data.app_id }),
				};
				break;
			case LoggingAction.IMAGE_PUBLISH:
				loggingData = {
					image_id: data.pictureid,
					pictureid: data.pictureid,
					...(data.origin && { origin: data.origin }),
					...(data.app_id && { app_id: data.app_id }),
				};
				break;
			case LoggingAction.IMAGE_DOWNLOAD:
				loggingData = {
					image_id: data.pic_id,
					pic_id: data.pic_id,
					origin: data.origin || this.dataWareHouse.origin || '',
					app_id: data.app_id ?? '',
					...(data.is_ai_video ? { is_ai_video: data.is_ai_video } : {}),
				};
				break;
			case LoggingAction.APP_VISIT: {
				const currentTab = getCurrentTab();
				const clickObject = getClickObject();
				const origin = localAESStorage.getItem(LSK.ORIGIN) || 'none';
				const otherParams =
					origin === 'search_result'
						? {
								search_content: localStorage.getItem(LSK.SEARCH_KEYWORD) || '',
								search_method: localStorage.getItem(LSK.SEARCH_METHOD) || '',
							}
						: origin === 'appgallery'
							? {
									gallery_id: data.gallery_id || '',
								}
							: {};

				const sortInfo = data.sort ? { sort: data.sort, first_view: data.firstView } : {};
				const appRelatedParams = data.is_video_gen ? { type: FeatureAtomType.VideoGenerate } : {};

				const currentId = data.app_id || '';
				const { isRecommendApp, source } = checkIsRecommendAppByData(currentId);
				const objectType = getObjectType({ isVideo: data.hasVideo, appCategory: data.appCategory });
				const recommendFields = isRecommendApp
					? {
							commend_label: source,
							app_source: 'onboarding_old',
						}
					: {};
				loggingData = {
					current_tab: currentTab || '',
					app_id: data.app_id || '',
					short_id: data.short_id || '',
					pub_id: data.pub_id || '',
					image_id: data.image_id || '',
					origin: origin || '',
					location1: isMobileOnly ? trackRecorder.source.location1 : 'web',
					location2: isMobileOnly ? trackRecorder.source.location2 : 'web',
					location: isMobileOnly ? trackRecorder.source.location || '' : 'web',
					enter_point: trackRecorder.source.generatePlace ?? 'detail',
					click_object: clickObject || 'none',
					object_type: objectType,
					tag_name: data.tagName || labelName || '',
					tag_id: data.tagId || labelId || '',
					ability_name: data.ability_name || '',
					user_scene: data.user_scene || '',
					is_slide_view: data.is_slide_view || false,
					...sortInfo,
					...appRelatedParams,
					...otherParams,
					trace_info: this.getTraceInfo(data.app_id as string) || '',
					app_source: data.app_source || '',
					...recommendFields,
				};

				break;
			}
			case LoggingAction.PIC_VISIT: {
				const searchWord = localStorage.getItem(LSK.SEARCH_KEYWORD) || 'none';
				loggingData = {
					...data,
					origin: data.origin,
					current_tab: this.dataWareHouse.origin,
					searchword: searchWord,
				};
				break;
			}
			case LoggingAction.PIC_LIKE:
				loggingData = {
					image_id: data.pictureid,
					pic_id: data.pictureid,
					...(data.is_ai_video ? { is_ai_video: data.is_ai_video } : {}),
				};
				break;
			case LoggingAction.APP_TAB: {
				loggingData = { presentation_mode: data.tab || '', current_tab: data.currentTab || '' };
				break;
			}
			case LoggingAction.SORTMODE_SWITCH:
				loggingData = { sortmode_mode: data.sort };
				break;
			case LoggingAction.USERPATH: {
				const preUrl = document.referrer ? document.referrer : '';
				const traceId = localStorage.getItem(LSK.TRACE_ID) || '';
				loggingData = {
					traceId: traceId,
					preUrl,
					...data,
				};
				break;
			}
			case LoggingAction.REGISTER: {
				defaultData = true;
				paramsData = { user_id: data.user_id };
				const inviter = sessionAESStorage.getItem(SSK.INVITE) || '';
				const referralRegister = inviter !== '' ? data.user_id : '';
				let googlePlayReferrer = localStorage.getItem(LSK.GOOGLE_PLAY_REFERRER) || '';
				const referrerDecodeInfo = localStorage.getItem(LSK.REFERRER_DECODE_RESULT) || '';
				const topAppId = sessionStorage.getItem(SSK.AD_APP_SHORT_ID);
				const adAppShortId = topAppId ? topAppId.split(',') : '';
				let isShared = false;
				let appDownloadOrigin = 'none';
				let aeoGroupingId = '';
				if (inviter) {
					const shareInfoObj = await ParamsEncryption.decrypt(inviter);
					isShared = true;
					appDownloadOrigin = shareInfoObj.type;
					// 如果share_token有广告id说明是裂变来的
					if (shareInfoObj.aeoGroupingId) {
						aeoGroupingId = shareInfoObj.aeoGroupingId;
					}
				} else {
					aeoGroupingId = getAeoGroupingId(referrerDecodeInfo);
					if (aeoGroupingId) {
						appDownloadOrigin = 'aeo';
					}
				}

				// *********** 处理googlePlayReferrer start ************
				if (googlePlayReferrer.includes('utm_source=share')) {
					googlePlayReferrer = googlePlayReferrer.replace(',', '&');
					const searchParams = new URLSearchParams(googlePlayReferrer);
					const shareInfo = searchParams.get('share_info');
					console.log('shareInfo share', shareInfo);
					if (shareInfo) {
						const shareInfoObj = await ParamsEncryption.decrypt(shareInfo);
						// 把 shareInfoObj json化之后并对特殊符号转义 替换之前的 share_info 参数值
						const shareInfoStr = JSON.stringify(shareInfoObj);
						const shareInfoStrEscaped = encodeURIComponent(shareInfoStr);
						googlePlayReferrer = googlePlayReferrer.replace(shareInfo, shareInfoStrEscaped);
						console.log('shareInfoObj share', shareInfoObj);
					}
				} else if (googlePlayReferrer.includes('utm_source=h5')) {
					// 非分享渠道进入a1点击吊起app 跳转过来的
					googlePlayReferrer = googlePlayReferrer.replaceAll(',', '&');
				}
				// *********** 处理googlePlayReferrer end ************

				loggingData = {
					is_visitor: data.is_visitor,
					user_id: data.user_id,
					method: data.method,
					inviter: inviter,
					referralRegister: referralRegister,
					location: this.dataWareHouse.location,
					tag_name: labelName || 'none',
					tag_id: labelId || 'none',
					google_play_referrer: googlePlayReferrer,
					aeoGroupingId,
					isShared,
					appDownloadOrigin,
					ad_app_short_id: adAppShortId,
				};

				userBehavior.setState({
					aeoGroupingId,
					appDownloadOrigin,
				});

				if (inviter) eventTracker.loggingEvent(LoggingAction.REFERRAL_LINK_SUCCESS);

				console.log('===注册埋点', {
					is_visitor: data.is_visitor,
					inviter: inviter,
					referralRegister: referralRegister,
					location: this.dataWareHouse.location,
					tag_name: labelName || 'none',
					tag_id: labelId || 'none',
					google_play_referrer: googlePlayReferrer,
					aeoGroupingId,
					isShared,
					appDownloadOrigin,
				});
				break;
			}
			case LoggingAction.AUTOLOGIN: {
				defaultData = true;
				loggingData = {
					...data,
					location: this.dataWareHouse.location,
				};
				break;
			}
			case LoggingAction.LOGIN_BUTTON_CLICK: {
				defaultData = true;
				loggingData = data;
				break;
			}
			case LoggingAction.BUILD_CLICK: {
				defaultData = true;
				loggingData = {
					is_vip: data.is_vip,
				};
				break;
			}
			case LoggingAction.BANNER_CLICK:
			case LoggingAction.CREATOR_SUPPORT_BANNER_CLICK_PC:
			case LoggingAction.LANDING_BUILD_CLICK:
				defaultData = true;
				break;
			case LoggingAction.REFERRAL_LINK_SUCCESS: {
				defaultData = true;
				break;
			}
			case LoggingAction.PROFILE_ENTER: {
				if (location.search.includes('source=share')) this.updateLogging({ userLocation: 'share' });
				loggingData = { userId: data.userId, location: this.dataWareHouse.userLocation };
				this.updateLogging({ userLocation: 'other' });
				break;
			}
			case LoggingAction.RANK_VISIT: {
				defaultData = true;
				loggingData = { location: this.dataWareHouse.rankLocation };
				sessionAESStorage.setItem(SSK.CURRENT_TAB, 'rank');
				break;
			}
			case LoggingAction.SHARE_BUTTON_CLICK: {
				const currentTab = getCurrentTab();
				if (data.type == 'profile') {
					loggingData = data;
					(loggingData as any).current_tab = currentTab;
					break;
				}
				const typeInfo = data.picid
					? {
							type: 'image',
							picid: data.picid,
							image_id: data.imageLongId,
							image_long_id: data.imageLongId,
						}
					: { type: 'app' };
				const idInfo = data.collectionId
					? { collection_id: data.collectionId }
					: {
							appid: data.appid || '',
							app_long_id: data.appLongId || '',
							app_id: data.app_id || '',
							trace_info: this.getTraceInfo(data.app_id as string),
						};
				loggingData = {
					...typeInfo,
					...idInfo,
					current_tab: currentTab,
					object: data.object,
					object_name: data.objectName,
					...(data.is_ai_video ? { is_ai_video: data.is_ai_video } : {}),
				};
				break;
			}

			case LoggingAction.APP_PUBLISH_CLICK: {
				loggingData = {
					appid: data.appid,
					app_id: data.app_id,
					build_from: data.forkFromId ? 'fork' : 'scratch',
					app_type: data.type,
				};
				break;
			}
			case LoggingAction.UNPUBLISH_CLICK:
			case LoggingAction.UNPUBLISH_SUCCESS: {
				const idInfo =
					data.object === 'app'
						? { appid: data.appid, app_id: data.app_id }
						: { image_id: data.picId, pic_id: data.picId };
				loggingData = { ...idInfo, object: data.object };
				break;
			}
			case LoggingAction.EXPOSURE: {
				defaultData = false;
				loggingData = {
					exposureList: data.arrList,
					tag_id: labelId,
					tag_name: labelName,
					...(data.search ?? {}),
				};
				const traceInfoList = data.arrList
					.filter((item: any) => !!item.trace_info)
					.map((item: any) => ({
						app_id: item.app_id,
						traceInfo: item.trace_info,
					}));
				this.saveTraceInfoList(traceInfoList);
				break;
			}
			case LoggingAction.IMAGE_EXPOSURE:
			case LoggingAction.BANNER_EXPOSURE: {
				defaultData = false;
				loggingData = {
					tag_id: labelId,
					tag_name: labelName,
					exposureList: data.arrList,
				};
				break;
			}
			case LoggingAction.BIG_IMAGE: {
				loggingData = {
					image_id: data.imageId,
					app_id: data.appId,
					origin: data.origin,
				};
				break;
			}
			case LoggingAction.Install_Now: {
				defaultData = true;
				break;
			}
			case LoggingAction.Add_To_Desktop_Load_success: {
				loggingData = {
					is_first_time: data.isFirstTime,
				};
				defaultData = true;
				break;
			}
			case LoggingAction.Add_To_Desktop_Download_Popup: {
				loggingData = {
					click_object: data.clickObject,
				};
				defaultData = true;
				break;
			}
			case LoggingAction.CHANGE:
			case LoggingAction.CREATOR_BONUS_CLICK:
			case LoggingAction.BACK_TO_TOP: {
				break;
			}
			case LoggingAction.SAVE_APP: {
				loggingData = {
					...data,
					trace_info: this.getTraceInfo(data.app_id as string),
				};
				break;
			}
			case LoggingAction.SEARCH:
			case LoggingAction.APP_EDIT_CLICK:
			case LoggingAction.FORK_APP:
			case LoggingAction.TASKLIST_CLICK:
			case LoggingAction.TASKLIST_ENTRANCE_CLICK:
			case LoggingAction.IMAGE_INPUT_RESULT_SWITCH:
			case LoggingAction.PRICING_POPUP:
			case LoggingAction.CREDITS_POPUP:
			case LoggingAction.PURCHASE_CREDITS_RESULT:
			case LoggingAction.PAY_PLAN_POPUP:
			case LoggingAction.PAY_RESULT:
			case LoggingAction.PRICING_PREMIUM_SUBSCRIBE_CLICK:
			case LoggingAction.PURCHASE_CREDITS_BUTTON_CLICK:
			case LoggingAction.GOOGLE_PLAY_POPUP_CLOSE_MANUALLY:
			case LoggingAction.PAY_CANCEL_RECOVERY:
			case LoggingAction.PAY_CANCEL_RECOVERY_CLICK:
			case LoggingAction.BANNER_CLICK_STARTCREATE:
			case LoggingAction.BANNER_CLICK_VIEWDETAILS:
			case LoggingAction.LOGIN_SUCCESS:
				loggingData = data;
				break;

			case LoggingAction.RATE_POPUP_SHOW:
			case LoggingAction.RATE_POPUP_CLOSE:
			case LoggingAction.RATE_POPUP_CLICK_GOTO_GOOGLE:
				break;

			case LoggingAction.RATE_POPUP_CLICK_SUBMIT:
			case LoggingAction.CHECK_IN_EXPOSURE:
			case LoggingAction.NEW_FEATURE_CONFIRM:
				loggingData = data;
				break;
			case LoggingAction.PREFERENCE_SELECTION_START:
				loggingData = {
					preference_names: data.preferenceNames,
					preference_ids: data.preferenceIds,
				};
				break;
			case LoggingAction.SURVEY_POPUP:
				loggingData = data;
				break;
			case LoggingAction.IMAGE_UPLOAD_CLICK: {
				const searchParams = getRecommendSearchParams(data.search);
				loggingData = {
					product_name: product,
					app_id: data.appId ?? '',
					user_type: isBuildPage() ? 'creator' : 'consumer',
					trace_info: data?.trace_info ?? eventTracker.getTraceInfo(data.appId) ?? '',
					user_scene: data.user_scene ?? '',
					ability_name: data.ability_name ?? '',
					...searchParams,
				};
				break;
			}
			case LoggingAction.IMAGE_UPLOAD_FRONT:
				loggingData = {
					product_name: product,
					app_id: data.appId,
					user_type: isBuildPage() ? 'creator' : 'consumer',
					file_path: data.filePath,
					failure_reason: data.failureReason,
					trace_info: eventTracker.getTraceInfo(data.appId),
				};
				break;
			case LoggingAction.LEAVE:
			case LoggingAction.H5_POPIP_SHOW:
			case LoggingAction.H5_POPIP_CLICK:
				loggingData = data;
				break;
			default:
				console.warn(`Unknown log type: ${action}`);
		}

		try {
			// 检查paramsData和loggingData的缺失值
			const missingParamKey = this.lostValues(paramsData);
			const missingLoggingKey = this.lostValues(loggingData);

			if (missingParamKey) {
				logger.error(
					LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER,
					`埋点 ${action} 缺少关键属性 ${missingParamKey}`,
					paramsData,
				);
				return;
			}

			if (missingLoggingKey) {
				logger.error(
					LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER,
					`埋点 ${action} 缺少关键属性 ${missingLoggingKey}`,
					loggingData,
				);
				return;
			}
			const option = this.getOption(action, paramsData, {
				...(defaultData === true ? this.getProperties() : {}),
				...loggingData,
			});
			this.addCount();
			if (!onlyGetData) {
				this.eventTracking(option);
			}
			const data = JSON.stringify(option);
			return { data };
		} catch (error) {
			logger.error(LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER, '请求发送失败:', error);
		}
	}

	public initializeTrackingCount(): void {
		sessionStorage.setItem(SSK.TRACKING_COUNT, '1');
	}

	public clearTrackingCount(): void {
		sessionStorage.removeItem(SSK.TRACKING_COUNT);
	}
	public updateChannel(searchStr: string = '', hasUserCount = false) {
		function createChannel(searchStr: string) {
			const urlParams = new URLSearchParams(searchStr);
			localStorage.setItem(LSK.SOURCE, urlParams.get('source') || '');
			localStorage.setItem(LSK.PLAN, urlParams.get('plan') || '');
			localStorage.setItem(LSK.TRACE_ID, urlParams.get('traceId') || '');
			localStorage.setItem(LSK.CREATE_DATE, new Date().toISOString());
		}
		function delateChannel() {
			localStorage.removeItem(LSK.CREATE_DATE);
			localStorage.removeItem(LSK.TRACE_ID);
		}

		const isLogin = useUserCreate.getState().getIsLogin();
		if (isLogin || hasUserCount) {
			delateChannel();
			return;
		}
		const urlParams = new URLSearchParams(searchStr);
		const sourceParam = urlParams.get('source');
		const planParam = urlParams.get('plan');
		const traceIdParam = urlParams.get('traceId');

		if (sourceParam || planParam || traceIdParam) {
			if (!isLogin) {
				createChannel(searchStr);
				return;
			}
		}
		// 删除超时的频道
		// FIXME: 用AES封装
		if (
			new Date().getTime() - new Date(localStorage.getItem(LSK.CREATE_DATE)!).getTime() >
			24 * 60 * 60 * 1000
		) {
			delateChannel();
		}
	}

	public updateLogging(payload: object) {
		this.dataWareHouse = {
			...this.dataWareHouse,
			...payload,
		};
	}

	public saveTraceInfoList(traceInfoList: { app_id: string; traceInfo: string }[]) {
		traceInfoList.forEach(({ app_id, traceInfo }) => {
			if (traceInfo) {
				this.dataWareHouse.traceInfoList.set(app_id, traceInfo);
			}
		});
	}

	public getTraceInfo(app_id: string) {
		return this.dataWareHouse.traceInfoList.get(app_id) || '';
	}

	public getUserVersion() {
		if (!isA1App) return 'non_app';
		return UserChannelVersionMap[useUserTypeStore.getState().userType];
	}

	private getOption(
		action: string,
		params: any = {},
		propertiesNew?: any,
	): eventTrackingApi.Option {
		const isLogin = useUserCreate.getState().getIsLogin();
		let userId = '';
		if (params.user_id) {
			userId = params.user_id;
		}
		const isTourist = useUserCreate.getState().getIsTourist();

		const option: eventTrackingApi.Option = {
			business_type: businessType,
			theme: this.getActionToTheme(action) || Theme.Behaviour,
			action,
			distinct_id: nanoid(),
			time: Date.now(), //UTC时间戳
			utc_time: getUTC8TimeString(), //UTC+8
			user_id: userId !== '' ? userId : isLogin ? useUserCreate.getState().getUserId() : '',
			hrefUrl: window.location.href,
			device_id: Tourist.deviceId,
			uniqueId:
				isLogin && action !== 'register' && action !== 'autologin' ? '' : this.getShortUUID(),
			sessionId: this.getUUID(),
			count: parseInt(sessionStorage.getItem(SSK.TRACKING_COUNT)!) || 0,
			properties: {
				is_visitor: isTourist,
				product: LowercaseProduct,
				app_version: this.getAppVersion(),
				user_version: this.getUserVersion(),
				device_type: getDevice(),
				device_link_id: this.getGoogleAdId(),
				vip_status: getUserVipStatus(),
				...getRecommendPageInfo(action),
				...propertiesNew,
			},
			...params,
		};

		return option;
	}

	private getActionToTheme(action: string) {
		let theme = null;
		Object.keys(actionToTheme).forEach((item) => {
			if (item.toLowerCase() == action.toLowerCase()) {
				theme = (actionToTheme as any)[item];
			}
		});
		return theme;
	}

	public getShortUUID() {
		let uniqueID = localStorage.getItem(LSK.UNIQUE_ID);
		if (!uniqueID) {
			uniqueID = nanoid(24);
			localStorage.setItem(LSK.UNIQUE_ID, uniqueID);
		}
		return uniqueID;
	}

	public getUUID() {
		let sessionId = sessionStorage.getItem(SSK.SESSION_ID);
		if (!sessionId) {
			sessionId = nanoid(10);
			sessionStorage.setItem(SSK.SESSION_ID, sessionId);
		}
		return sessionId;
	}

	public getProperties() {
		return {
			source: localStorage.getItem(LSK.SOURCE) || '',
			plan: localStorage.getItem(LSK.PLAN) || '',
			traceId: localStorage.getItem(LSK.TRACE_ID) || '',
			firstenterurl: this.getFirstVisitRoute(),
			referrerurl: this.getFirstVisitRefRoute(),
		};
	}

	private checkAndSend() {
		if (this.isSending) return;

		const currentTime = Date.now();
		// 检查队列中是否存在 generate_success 事件
		const sendNow = this.eventQueue.some((event) =>
			['generate_success', 'is_read'].includes(event.action),
		);

		const len = this.eventQueue.length;

		const shouldSend =
			sendNow ||
			len >= this.SEND_COUNT ||
			(len > 0 && currentTime - this.lastSendTime >= this.SEND_INTERVAL);

		if (shouldSend) {
			this.isSending = true;
			// 将数据分批处理
			const dataToSend = this.eventQueue.slice(); // 复制当前的埋点数据
			// 提前清除已发送的埋点数据，后续可再完善
			this.clearEventQueue();

			post({
				url: A1_DATA_WAREHOUSE,
				data: JSON.stringify(dataToSend),
				config: { withCredentials: false },
			})
				// 清除已发送的埋点数据
				.then(() => (this.lastSendTime = currentTime))
				.catch((error) => {
					this.rollbackQueue(dataToSend);
					logger.error(LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER, '请求发送失败:', error);
				})
				.finally(() => {
					this.isSending = false;
				});
		}
	}

	/**
	 * 写入埋点日志 仅在测试环境写入，方便测试
	 * @param data 埋点数据
	 */

	private writeEventLog(data: eventTrackingApi.Option) {
		// 生产环境不写入日志
		if (!isOpenDebugMode) return;
		try {
			const logItem = {
				id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
				timestamp: Date.now(),
				business_type: data.business_type,
				theme: data.theme,
				action: data.action,
				properties: data.properties,
				raw: data,
			};
			if (!window.__A1_EVENT_LOGS__) window.__A1_EVENT_LOGS__ = [];
			window.__A1_EVENT_LOGS__.push(logItem as any);
			// 控制最大长度，防止内存占用过大
			if (window.__A1_EVENT_LOGS__.length > 500) {
				window.__A1_EVENT_LOGS__ = window.__A1_EVENT_LOGS__.slice(-500);
			}
		} catch (error) {
			console.error('写入日志失败:', error);
		}
	}

	private eventTracking(data: eventTrackingApi.Option) {
		console.log(`${data.action}埋点，埋点数据为：`, cloneDeep(data));

		if (isGoogleBot()) return;

		const host = A1_DATA_WAREHOUSE;
		try {
			// 检查是否缺少关键信息
			const missingField = this.lostDataValues(data);
			if (missingField) {
				logger.error(LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER, `缺少埋点信息: ${missingField}`, data);
				return;
			}

			// 如果数据没有缺少关键信息，则check发送类型，并发送请求
			if (this.SEND_AS_ARRAY && !SINGLE_SHOT_TYPE.includes(data.action as LoggingAction)) {
				// 将数据添加到数组
				this.addEventToQueue(data);
			} else {
				post({
					url: host,
					data: JSON.stringify(data),
					config: { withCredentials: false },
				}).catch((error) => {
					logger.error(LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER, '请求发送失败:', error);
				});
				this.writeEventLog(data);
			}
		} catch (error) {
			logger.error(LOGGER_SPACIAL_SYMBOL.EVENT_LOGGER, '请求处理失败:', error);
		}
	}

	private readonly requiredFields = [
		'business_type',
		'theme',
		'action',
		'distinct_id',
		'time',
		'sessionId',
		'',
	];

	private lostDataValues = (obj: any): string | null => {
		// 深层遍历所有属性，不能为 null 或 undefined
		const deepCheck = (obj: any): string | null => {
			for (const key in obj) {
				if (obj.hasOwnProperty(key)) {
					if (obj[key] === undefined || obj[key] === null) {
						return key;
					} else if (typeof obj[key] === 'object') {
						const missingField = deepCheck(obj[key]);
						if (missingField) {
							return missingField;
						}
					}
				}
			}
			return null;
		};

		// 检查深层遍历结果
		const deepCheckResult = deepCheck(obj);
		if (deepCheckResult) {
			return deepCheckResult;
		}

		// 检查 user_id 和 uniqueId 是否同时为空
		if (obj.user_id === '' && obj.uniqueId === '') {
			return `user_id 和 uniqueId 不能同时为空`;
		}

		// 检查登陆后才能进行的事件
		if (obj.action in LOGIN_ACTION) {
			if (obj.user_id === '') {
				return `需要登录的埋点事件出现了userId为空值的情况`;
			}
		}

		// 检查路径源信息是否正确
		if (
			obj.user_id === '' ||
			obj.action === LoggingAction.REGISTER ||
			obj.action === LoggingAction.AUTOLOGIN
		) {
			const { source, plan, traceId } = this.getProperties();
			const checkId = useUserCreate.getState().getIsLogin();
			// 检查路径源参数
			if (source === '' || plan === '' || traceId === '') {
				console.log(
					`路径源参数错误：source=${source || '未提供'}，plan=${plan || '未提供'}，traceId=${traceId || '未提供'}。登录状态是：${checkId}`,
				);
				return null;
			}
		}
		// 检查必须字段是否为空
		for (const field of this.requiredFields) {
			if (obj[field] === undefined || obj[field] === null || obj[field] === '') {
				return field;
			}
		}

		return null;
	};

	// FIXME: 用AES封装
	private getFirstVisitRoute() {
		const visitRecordstr = localStorage.getItem(LSK.VISIT_RECORD);
		if (visitRecordstr) {
			const visitRecord = JSON.parse(visitRecordstr);
			const currentTime = Math.floor(Date.now() / 1000);
			if (currentTime - visitRecord.timestamp <= 24 * 60 * 60) {
				return visitRecord.route;
			}
		}
		localStorage.setItem(
			LSK.VISIT_RECORD,
			JSON.stringify({
				route: window.location.pathname,
				timestamp: Math.floor(Date.now() / 1000),
			}),
		);
		return window.location.pathname;
	}

	// FIXME: 用AES封装
	private getFirstVisitRefRoute() {
		const visitRecordstr = localStorage.getItem(LSK.VISIT_REF_RECORD);
		if (visitRecordstr) {
			const visitRecord = JSON.parse(visitRecordstr);
			const currentTime = Math.floor(Date.now() / 1000);
			if (currentTime - visitRecord.timestamp <= 24 * 60 * 60) {
				return visitRecord.route;
			}
		}
		localStorage.setItem(
			LSK.VISIT_REF_RECORD,
			JSON.stringify({
				route: document.referrer,
				timestamp: Math.floor(Date.now() / 1000),
			}),
		);
		return document.referrer;
	}

	// 检查传入对象中是否存在属性值为undefined或null的属性
	private lostValues = (obj: any): string | null => {
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				if (obj[key] === undefined || obj[key] === null) {
					return key;
				}
			}
		}
		return null;
	};

	private addCount(): void {
		let trackingCount = parseInt(sessionStorage.getItem(SSK.TRACKING_COUNT) as string) || 1;
		trackingCount++;
		sessionStorage.setItem(SSK.TRACKING_COUNT, trackingCount.toString());
	}
}

const eventTracker = new EventTracker();

HttpDependency.inject({
	trackerOptimize(data) {
		eventTracker.loggingEvent(LoggingAction.OPTIMIZE, data);
	},
});

export default eventTracker;
